import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, testimonials } from '../mockData';
import * as LucideIcons from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100 via-blue-50 to-blue-100 -z-10"></div>
        
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            {/* Prominent Logo */}
            <div className="mb-8 flex justify-center">
              <img 
                src="/redtop-logo.png" 
                alt="Redtop Contracting & Consulting" 
                className="h-32 md:h-40 lg:h-48 w-auto drop-shadow-xl"
              />
            </div>
            
            <div className="inline-block mb-4 px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-medium">
              Professional Home Improvement Services
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Transform Your Home with
              <span className="text-red-600"> Considerate & Comprehensive Construction Services</span> with Reverence for Your Living Space!
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              From kitchen remodels to complete home renovations, Redtop Contracting & Consulting brings quality, reliability, and excellence to every project.
            </p>

            {/* Featured Videos */}
            <div className="mb-10 flex flex-col items-center gap-6">
              {/* Video 1 */}
              <div className="max-w-md rounded-2xl overflow-hidden shadow-2xl bg-gray-900">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  poster="/gallery-photos/kitchen-featured.jpg"
                  className="w-full h-auto"
                >
                  <source src="/featured-video-web.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              
              {/* Video 2 */}
              <div className="max-w-md rounded-2xl overflow-hidden shadow-2xl bg-gray-900">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  className="w-full h-auto"
                >
                  <source src="/featured-video-2-web.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Link to="/contact">
                  Get Free Quote
                  <ArrowRight className="ml-2" size={20} />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-blue-800 text-blue-800 hover:bg-blue-800 hover:text-white text-lg px-8 py-6 transition-all duration-300"
              >
                <Link to="/gallery">View Our Work</Link>
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-600" size={20} />
                <span>Licensed & Insured</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-600" size={20} />
                <span>20+ Years in Commercial & Residential Construction</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-600" size={20} />
                <span>Free Consultations</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive home improvement solutions tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => {
              const IconComponent = LucideIcons[service.icon];
              return (
                <Link to={`/gallery?category=${service.id}`} key={service.id}>
                  <Card
                    className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-red-200 cursor-pointer overflow-hidden h-full"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4">
                        {IconComponent && (
                          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-lg">
                            <IconComponent className="text-red-600" size={24} />
                          </div>
                        )}
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-red-600 transition-colors">
                        {service.name}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {service.description}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why Choose Redtop?
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Experience the difference of working with true professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-8 rounded-2xl bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3">Quality Workmanship</h3>
              <p className="text-blue-100">
                Expert craftsmanship with attention to every detail, ensuring lasting results you'll love.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3">Transparent Pricing</h3>
              <p className="text-blue-100">
                No hidden fees or surprises. Clear, upfront quotes so you know exactly what to expect.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3">Timely Completion</h3>
              <p className="text-blue-100">
                We respect your time and schedule, delivering projects on time without compromising quality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Don't just take our word for it
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={20} className="text-amber-500 fill-amber-500" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.project}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Home?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-red-50">
            Get started with a free consultation and quote today. Let's bring your vision to life!
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white text-red-600 hover:bg-gray-100 text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all duration-300"
          >
            <Link to="/contact">
              Schedule Free Consultation
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;